import {
  WSActions,
  WSCard,
  WSElement,
  WSInputNumber,
  WSList,
  WSSectionToolbar,
  WSText,
  useIsMobile,
  useWSSnackbar
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import {
  ExternalBankAccountStatus,
  TransferDirection,
  TransferMethod
} from "@wingspanhq/wingspan-api-sdk";
import { Nullable } from "primereact/ts-helpers";
import { useMemo, useState } from "react";
import { ErrorAlert } from "@components/ErrorAlert";
import {
  queriesExternalBankAccounts,
  queriesInternalAccounts,
  queriesTransfers
} from "@query";
import { externalBankAccountsApi } from "@api";
import { IdempotencyKey } from "@utils/idempotencyKey";
import { selectorExternalBankAccountFullName } from "@selectors/selectorExternalBankAccountFullName";
import { useQueryOwnPayoutSettings } from "@hooks/useQueryOwnPayoutSettings";
import { WSQueryCache } from "@ws-react-query";
import { useCurrentAccountName } from "../../../../v3/providers/CurrentAccountNameProvider";
import { FromTo } from "../../../../shared/components/FromTo";
import { selectorActiveBankingAccount } from "../../../../shared/selectors/selectorActiveBankingAccount";
import { selectorActiveTaxWithholdingAccount } from "../../../../shared/selectors/selectorActiveTaxWithholdingAccount";
import { selectorIsPlatformBalanceAccount } from "../../../PlatformBalance/selectors/selectorIsPlatformBalanceAccount";
import { LabelMethod } from "../../../Settings/components/LabelMethod";
import { MethodValue } from "../../../Settings/components/ManageMethods/types";
import { TransferBalance, TransferType } from "../../types";
import { useModalAuthorizeACH } from "../ModalAuthorizeACH";
import { DropdownAccount } from "./DropdownAccount";
import { useModalReconnectAccount } from "./ModalReconnectAccount";

const BALANCE_LABELS: Record<TransferBalance, string> = {
  wallet: "Wingspan Wallet",
  taxWithholdings: "Tax Withholdings",
  platform: "Platform balance"
};

function getTransferDescription(
  type: TransferType,
  balance: TransferBalance
): string {
  const label = BALANCE_LABELS[balance];
  return type === "deposit" ? `Transfer to ${label}` : `Transfer from ${label}`;
}

type Props = {
  balance: TransferBalance;
  type: TransferType;
  onCancel: () => void;
  onSuccess: () => void;
};

export const FormTransfer: React.FC<Props> = ({
  type,
  balance,
  onCancel,
  onSuccess
}) => {
  const isMobile = useIsMobile();
  const { openSnackbar } = useWSSnackbar();

  const queryInternalAccounts =
    queriesInternalAccounts.listInternalAccounts.useQuery();
  const queryPayoutSettings = useQueryOwnPayoutSettings();

  const accountName = useCurrentAccountName().resolvedName;
  const modalAuthorizeACH = useModalAuthorizeACH();
  const modalReconnectAccount = useModalReconnectAccount();

  const internalAccount = useMemo(() => {
    switch (balance) {
      case "wallet":
        return queryInternalAccounts.data?.data?.find(
          selectorActiveBankingAccount
        );
      case "taxWithholdings":
        return queryInternalAccounts.data?.data?.find(
          selectorActiveTaxWithholdingAccount
        );
      case "platform":
        return queryInternalAccounts.data?.data?.find(internalAccount =>
          selectorIsPlatformBalanceAccount(
            internalAccount,
            queryPayoutSettings.data
          )
        );
      default:
        return;
    }
  }, [queryInternalAccounts.data, queryPayoutSettings.data, balance]);

  const [amount, setAmount] = useState<number | null>(null);
  const [selectedMethod, setSelectedMethod] =
    useState<Nullable<MethodValue>>(null);

  const queryCounterpartyAccount =
    queriesExternalBankAccounts.getExternalBankAccount.useQuery(
      { bankAccountId: selectedMethod?.id ?? "" },
      {
        enabled: !!selectedMethod && selectedMethod.type === "Account"
      }
    );

  const transferSummary = useMemo(() => {
    if (!selectedMethod) return null;

    let counterpartyName: string | undefined;

    if (selectedMethod.type === "Account" && queryCounterpartyAccount.data) {
      counterpartyName = selectorExternalBankAccountFullName(
        queryCounterpartyAccount.data
      );
    } else if (selectedMethod.type === "InternalAccount") {
      const counterpartyInternalAccount =
        queryInternalAccounts.data?.data?.find(
          ia => ia.id === selectedMethod.id
        );

      if (counterpartyInternalAccount) {
        counterpartyName =
          counterpartyInternalAccount.type === "TaxWithholding"
            ? "Tax Withholdings"
            : "Wingspan Wallet";
      }
    }

    if (!counterpartyName) return null;

    return type === "withdrawal"
      ? `Transfer will be sent to ${counterpartyName}`
      : `Transfer will be sent from ${counterpartyName}`;
  }, [
    selectedMethod,
    queryCounterpartyAccount.data,
    queryInternalAccounts.data,
    type
  ]);

  const [transfer, transferMeta] = queriesTransfers.createTransfer.useMutation({
    onSuccess: () => {
      openSnackbar({
        message: "Transfer successfully initiated"
      });
      onSuccess();
    },
    onError: () => {
      // PLA-3450: a Wingspan Wallet withdrawal to a bank account can error on
      // the FE while the transfer actually succeeded on the backend. The
      // mutation's `dependencies` only invalidate on success, so refetch the
      // balance/transactions here too — otherwise the wallet silently shows
      // stale numbers until the user reloads the page.
      if (type === "withdrawal" && balance === "wallet") {
        WSQueryCache.invalidateQueries(
          queriesInternalAccounts.getInternalAccountBalance.queryKeyBase
        );
        WSQueryCache.invalidateQueries(
          queriesInternalAccounts.listInternalAccountTransactions.queryKeyBase
        );
      }
    }
  });

  const handleCompleteTransfer = async () => {
    if (!selectedMethod) {
      console.assert(false, "selectedMethod is not set");
      return;
    }

    if (!amount) {
      console.assert(false, "amount is not set");
      return;
    }

    if (!internalAccount) {
      console.assert(false, "internalAccount is not set");
      return;
    }

    // Show ach modal for external accounts

    if (type === "deposit" && selectedMethod.type === "Account") {
      const authorized = await modalAuthorizeACH.open({
        accountId: selectedMethod.id,
        balance,
        amount,
        accountName
      });

      if (!authorized) {
        return;
      }
    }

    if (
      type === "deposit" &&
      selectedMethod.type === "Account" &&
      queryCounterpartyAccount.data?.status ===
        ExternalBankAccountStatus.DISCONNECTED
    ) {
      try {
        const token =
          await externalBankAccountsApi.reconnectExternalBankAccount({
            bankAccountId: selectedMethod.id,
            bankAccountReconnectRequest: {}
          });

        const isReconnected = await modalReconnectAccount.open({
          token: token.linkToken
        });

        if (!isReconnected) {
          return;
        }
      } catch (error) {
        console.error(error);
        return;
      }
    }

    const isDeposit = type === "deposit";

    // Deposit moves money into the internal account (counterparty → internal);
    // withdrawal moves it out (internal → counterparty).
    const direction = isDeposit
      ? TransferDirection.DEPOSIT
      : TransferDirection.WITHDRAWAL;
    const sourceAccountId = isDeposit ? selectedMethod.id : internalAccount.id;
    const destinationAccountId = isDeposit
      ? internalAccount.id
      : selectedMethod.id;

    const idempotencyKey = new IdempotencyKey({
      direction,
      amount: String(amount),
      sourceAccountId,
      destinationAccountId
    });

    transfer(
      {
        idempotencyKey: idempotencyKey.value,
        transferCreate: {
          amount: Number(amount),
          currency: internalAccount.currency,
          direction,
          description: getTransferDescription(type, balance),
          transferMethod: TransferMethod.ACH,
          sourceAccountId,
          destinationAccountId
        }
      },
      {
        // Destroy only once the transfer succeeds; until then every retry of this
        // identical request reuses the key and is deduped server-side.
        onSuccess: () => idempotencyKey.destroy()
      }
    );
  };

  const [showConfirmation, setShowConfirmation] = useState(false);

  const balancePreview = internalAccount && (
    <LabelMethod
      type="InternalAccount"
      id={internalAccount.id}
      showBalance={type === "withdrawal"}
    />
  );

  const account =
    showConfirmation && selectedMethod ? (
      <LabelMethod type={selectedMethod.type} id={selectedMethod.id} />
    ) : (
      <DropdownAccount
        balance={balance}
        type={type}
        value={selectedMethod}
        onChange={setSelectedMethod}
      />
    );

  return (
    <WSList gap="2XL">
      <WSElement>
        <WSSectionToolbar
          title={
            type === "deposit"
              ? `Transfer to ${
                  balance === "wallet" ? "Wallet" : "Tax withholdings"
                }`
              : "Transfer to bank account"
          }
        />
        <WSText.ParagraphSm color="gray500">
          How much would you like to transfer?
        </WSText.ParagraphSm>
      </WSElement>

      <WSCard header="Amount">
        <WSList gap="XL">
          {showConfirmation ? (
            <WSText.Heading1 color="gray500" weight="book">
              {formatMoney(amount!, internalAccount?.currency || "USD")}
            </WSText.Heading1>
          ) : (
            <WSInputNumber
              size="L"
              value={amount}
              onChange={setAmount}
              currency={internalAccount?.currency || "USD"}
              maxFractionDigits={2}
              // Without an explicit inputMode, PrimeReact derives "numeric" from
              // mode="decimal" + no minFractionDigits, which gives mobile a
              // digits-only keypad (no decimal point). Force "decimal" so cents
              // are enterable on mobile (PLA-3161); desktop is unaffected.
              inputMode="decimal"
            />
          )}

          <FromTo
            fromLabel="From"
            fromContent={type === "deposit" ? account : balancePreview}
            toLabel="To"
            toContent={type === "deposit" ? balancePreview : account}
          />

          {transferSummary && (
            <WSText.ParagraphSm color="gray400" align="center">
              {transferSummary}
            </WSText.ParagraphSm>
          )}
        </WSList>
      </WSCard>

      <ErrorAlert context="PendingWithholding" error={transferMeta.error} />

      {showConfirmation ? (
        <WSActions
          alignment="fill"
          buttons={[
            {
              kind: "Secondary",
              label: "Cancel",
              onClick: () => {
                setShowConfirmation(false);
              }
            },
            {
              label: "Complete transfer",
              onClick: handleCompleteTransfer,
              loading: transferMeta.isLoading
            }
          ]}
        />
      ) : (
        <WSActions
          alignment="fill"
          buttons={[
            {
              kind: "Secondary",
              label: "Cancel",
              onClick: onCancel
            },
            {
              label: "Continue",
              onClick: () => {
                setShowConfirmation(true);
              },
              disabled: !selectedMethod || !amount
            }
          ]}
        />
      )}
    </WSList>
  );
};
